import CenteredCircularProgress from 'components/CenteredCircularProgress';
import { useAnalyzer } from 'features/analyzers/api/getAnalyzer';
import { useSnapshot } from 'features/snapshots/api/getSnapshot';
import {
  getGridColor,
  getMarkerColor,
  getTickColor,
} from 'features/spectrumChart/utils/colors';
import useInLightMode from 'hooks/useInLightMode';
import 'chart.js/auto';
import { Chart } from 'react-chartjs-2';
import { useTrendline } from './api/getTrendline';

interface Props {
  snapshotId: number;
  interval: string;
}

const TrendlineChart = ({ snapshotId, interval }: Props) => {
  const inLightMode = useInLightMode();
  const { data: snapshot } = useSnapshot({ snapshotId });
  const { markers } = snapshot || {};
  const { data: analyzer } = useAnalyzer({
    analyzerId: snapshot?.analyzer_id || -1,
  });
  const { data: trendlineData, isLoading } = useTrendline({
    snapshotId,
    interval: interval.toLowerCase(),
    config: { refetchInterval: 300000 },
  });
  const timeUnit = interval === 'Day' ? ('hour' as 'hour') : ('day' as 'day');
  const historyFound =
    trendlineData?.length === 0
      ? '(No History or Trendline Markers Found)'
      : '';

  const getMarkerName = (index: number) => {
    const marker = markers?.find((marker) => marker.index === index);
    if (!marker) return undefined;
    return marker?.name;
  };

  const spanGap = () => {
    if (interval === 'Day') {
      return 1000 * 60 * 10;
    } else if (interval === 'Week') {
      return 1000 * 60 * 30;
    } else if (interval === 'Month') {
      return 1000 * 60 * 60 * 2;
    } else if (interval === 'Year') {
      return 1000 * 60 * 60 * 12;
    }
  };

  const data = {
    labels: [],
    datasets: [
      {
        label: getMarkerName(100) || 'Trendline 1',
        data: trendlineData,
        borderColor: getMarkerColor(0, inLightMode),
        backgroundColor: getMarkerColor(0, inLightMode),
        fill: false,
        borderWidth: 2,
        tension: 0.2,
        pointRadius: 0.8,
        parsing: {
          yAxisKey: 'amplitude1',
        },
      },
      {
        label: getMarkerName(101) || 'Trendline 2',
        data: trendlineData,
        borderColor: getMarkerColor(1, inLightMode),
        backgroundColor: getMarkerColor(1, inLightMode),
        borderWidth: 2,
        tension: 0.2,
        pointRadius: 0.8,
        parsing: {
          yAxisKey: 'amplitude2',
        },
      },
      {
        label: getMarkerName(102) || 'Trendline 3',
        data: trendlineData,
        borderColor: getMarkerColor(2, inLightMode),
        backgroundColor: getMarkerColor(2, inLightMode),
        borderWidth: 2,
        tension: 0.2,
        pointRadius: 0.8,
        parsing: {
          yAxisKey: 'amplitude3',
        },
      },
      {
        label: getMarkerName(103) || 'Trendline 4',
        data: trendlineData,
        borderColor: getMarkerColor(3, inLightMode),
        backgroundColor: getMarkerColor(3, inLightMode),
        borderWidth: 2,
        tension: 0.2,
        pointRadius: 0.8,
        parsing: {
          yAxisKey: 'amplitude4',
        },
      },
    ],
  };

  const options = {
    responsive: true,
    spanGaps: spanGap(),
    fill: false,
    animation: {
      duration: 0,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${analyzer?.name} ${snapshot?.name} - ${interval} ${historyFound}`,
        color: getTickColor(inLightMode),
      },
    },
    parsing: {
      xAxisKey: 'timestamp',
    },
    interaction: {
      intersect: false,
      mode: 'index' as 'index',
    },
    scales: {
      y: {
        title: {
          display: true,
          text: 'dBm',
          color: getTickColor(inLightMode),
        },
        ticks: {
          color: getTickColor(inLightMode),
        },
        grid: {
          color: getGridColor(inLightMode),
        },
      },
      x: {
        type: 'time' as 'time',
        time: {
          unit: timeUnit,
        },
        grid: {
          color: getGridColor(inLightMode),
        },
        ticks: {
          color: getTickColor(inLightMode),
          display: true,
        },
      },
    },
  };

  if (isLoading) {
    return <CenteredCircularProgress />;
  }

  return <Chart type='line' options={options} data={data} />;
};

export default TrendlineChart;
